import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  openDialog,
} from 'platform/components';
import {Text, VStack} from 'platform/foundation';
import {object} from 'yup';

import {isNotNil} from 'ramda-adjunct';

import {useCreateVehicleAlbumMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId, yupString} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';

interface FormValues {
  description: string;
}

interface VehicleCreateAlbumButtonProps extends RequiredTestIdProps {
  vehicleId: string;
  serviceCaseId?: string;
  onComplete?: () => void;
}

export function VehicleCreateAlbumButton(props: VehicleCreateAlbumButtonProps) {
  const [createVehicleAlbum] = useCreateVehicleAlbumMutation();

  const isServiceCase = isNotNil(props.serviceCaseId);

  const onSubmit: FormSubmitHandler<FormValues> = (values) =>
    createVehicleAlbum({
      createVehicleAlbumRequestBody: {
        name: isServiceCase ? 'PHOTOS_SERVICE_CASE' : 'PHOTOS_CUSTOM',
        description: values.description,
        vehicleId: props.vehicleId,
        serviceCaseId: props.serviceCaseId ?? null,
        canHaveCover: true,
      },
    })
      .unwrap()
      .then(closeCurrentDialog)
      .then(props.onComplete)
      .catch(handleApiError);

  const onCreateAlbumButtonClick = () => {
    openDialog(
      <VStack spacing={4}>
        <Text size="small" color="secondary">
          {i18n.t('entity.vehicle.albums.dialog.text')}
        </Text>
        <Form<FormValues> onSubmit={onSubmit} schema={schema}>
          {(control) => (
            <VStack spacing={4}>
              <FormField
                control={control}
                name="description"
                type="text"
                label={i18n.t('entity.vehicle.labels.albumName')}
                isRequired
              />
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.cancel')}
                  variant="secondary"
                  onClick={closeCurrentDialog}
                />
                <FormButton
                  title={i18n.t('general.actions.save')}
                  type="submit"
                  control={control}
                />
              </ButtonGroup>
            </VStack>
          )}
        </Form>
      </VStack>,
      {
        title: i18n.t('entity.vehicle.albums.create'),
        size: 'small',
      }
    );
  };

  return (
    <Button
      data-testid={suffixTestId('vehicleCreateAlbumButton', props)}
      title={i18n.t('entity.vehicle.albums.create')}
      leftIcon="image/photo_album"
      variant="outlined"
      onClick={onCreateAlbumButtonClick}
    />
  );
}

const schema = object({
  description: yupString.required(),
});
